/**
 * Analytics Dashboard Page
 *
 * Comprehensive backup analytics with multiple views:
 * - Overview: General stats and quick insights
 * - Compliance: SLA monitoring and breach tracking
 * - Timeline: Resource protection status
 * - Resources: Detailed resource information
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileJson, FileSpreadsheet } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { ComplianceDashboard } from '@/components/analytics/ComplianceDashboard'
import { ProtectionTimeline } from '@/components/analytics/ProtectionTimeline'
import { apiClient } from '@/lib/api-client'
import {
  exportAnalyticsReport,
  exportComplianceReport,
  exportResourceTimeline,
  type AnalyticsExport,
  type ComplianceExport,
  type TimelineExport
} from '@/lib/exportUtils'
import {
  calculateHistoricalTrends,
  calculateProtectionTimeline,
  generateSLAReport,
  identifyAnomalies
} from '@/services/analyticsService'
import type { Task, Resource, Archive } from '@/types'

function OverviewTab({
  tasks,
  resources,
  isLoading
}: {
  tasks: Task[]
  resources: Resource[]
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const trends = calculateHistoricalTrends(tasks, 'week')
  const timeline = calculateProtectionTimeline(resources, tasks)
  const anomalies = identifyAnomalies(tasks)

  const atRiskCount = timeline.filter((t: any) => t.status === 'at-risk').length
  const warningCount = timeline.filter((t: any) => t.status === 'warning').length

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{trends.overall.successRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {trends.overall.successCount} of {trends.overall.totalTasks} tasks successful
            </p>
            {trends.trends.direction !== 'stable' && (
              <p className={`text-sm mt-2 ${
                trends.trends.direction === 'improving' ? 'text-green-600' : 'text-red-600'
              }`}>
                {trends.trends.direction === 'improving' ? '↑' : '↓'} {Math.abs(trends.trends.change).toFixed(1)}% from previous period
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Protected Resources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{resources.length}</div>
            <div className="flex gap-2 mt-2 text-sm">
              {atRiskCount > 0 && (
                <span className="text-red-600">{atRiskCount} at risk</span>
              )}
              {warningCount > 0 && (
                <span className="text-yellow-600">{warningCount} warnings</span>
              )}
              {atRiskCount === 0 && warningCount === 0 && (
                <span className="text-green-600">All healthy</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Anomalies Detected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{anomalies.summary.totalAnomalies}</div>
            {anomalies.summary.totalAnomalies > 0 ? (
              <p className="text-xs text-yellow-600 mt-1">
                {anomalies.summary.failureCount} failure sequences,{' '}
                {anomalies.summary.durationCount} duration anomalies
              </p>
            ) : (
              <p className="text-xs text-green-600 mt-1">
                All operations within normal parameters
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Trends</CardTitle>
          <CardDescription>Success rate over the past weeks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {trends.periods.slice(-8).map((period: any) => {
              const maxTotal = Math.max(...trends.periods.map((p: any) => p.total))
              const width = (period.total / maxTotal) * 100

              return (
                <div key={period.timestamp} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>{new Date(period.date).toLocaleDateString()}</span>
                    <span className={period.successRate >= 95 ? 'text-green-600' : 'text-red-600'}>
                      {period.successRate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        period.successRate >= 95 ? 'bg-green-600' : 'bg-red-600'
                      }`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Anomalies Alert */}
      {anomalies.suddenFailures.length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20">
          <CardHeader>
            <CardTitle className="text-red-700 dark:text-red-400">
              Critical: Failure Sequences Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {anomalies.suddenFailures.slice(0, 3).map((failure: any, i: any) => (
                <div key={i} className="p-3 bg-white dark:bg-gray-900 rounded border">
                  <p className="font-medium">
                    {failure.failureCount} consecutive failures
                    {failure.ongoing && <span className="ml-2 text-red-600">⚠️ ONGOING</span>}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Resources: {failure.affectedResources.join(', ')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(failure.startDate).toLocaleString()} to{' '}
                    {new Date(failure.endDate).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export function AnalyticsDashboardPage() {
  const [activeTab, setActiveTab] = useState('overview')

  // Fetch data
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['analytics', 'tasks'],
    queryFn: () => apiClient.get<Task[]>('/tasks'),
    refetchInterval: 30000 // Refresh every 30 seconds
  })

  const { data: resourcesData, isLoading: resourcesLoading } = useQuery({
    queryKey: ['analytics', 'resources'],
    queryFn: () => apiClient.get<Resource[]>('/resources'),
    refetchInterval: 60000 // Refresh every minute
  })

  const { isLoading: archivesLoading } = useQuery({
    queryKey: ['analytics', 'archives'],
    queryFn: () => apiClient.get<Archive[]>('/archives'),
    refetchInterval: 60000 // Refresh every minute
  })

  const tasks = tasksData?.data || []
  const resources = resourcesData?.data || []
  // Archives available for future use (forecasting, storage analysis)
  // const archives = archivesData?.data || []
  const isLoading = tasksLoading || resourcesLoading || archivesLoading

  // Export handlers
  const handleExportOverview = (format: 'json' | 'csv') => {
    const trends = calculateHistoricalTrends(tasks, 'week')

    const exportData: AnalyticsExport = {
      exportDate: new Date().toISOString(),
      summary: {
        totalTasks: trends.overall.totalTasks,
        successRate: trends.overall.successRate,
        trend: trends.trends.direction
      },
      periods: trends.periods.map((p: any) => ({
        date: p.date,
        successRate: p.successRate,
        total: p.total
      }))
    }

    exportAnalyticsReport(exportData, format)
  }

  const handleExportCompliance = (format: 'json' | 'csv') => {
    const report = generateSLAReport(tasks, 95, 'week')

    const exportData: ComplianceExport = {
      exportDate: new Date().toISOString(),
      overall: report.overall,
      breaches: report.breaches,
      periods: report.periods
    }

    exportComplianceReport(exportData, format)
  }

  const handleExportTimeline = (format: 'json' | 'csv') => {
    const timeline = calculateProtectionTimeline(resources, tasks)

    const exportData: TimelineExport = {
      exportDate: new Date().toISOString(),
      resources: timeline.map((item: any) => ({
        ...item,
        successRate: item.totalBackups > 0
          ? (item.successfulBackups / item.totalBackups) * 100
          : 0
      }))
    }

    exportResourceTimeline(exportData, format)
  }

  const getExportHandler = () => {
    switch (activeTab) {
      case 'overview':
        return handleExportOverview
      case 'compliance':
        return handleExportCompliance
      case 'timeline':
        return handleExportTimeline
      default:
        return handleExportOverview
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Backup Analytics</h1>
          <p className="text-muted-foreground">
            Comprehensive monitoring, trends, and compliance reporting
          </p>
        </div>

        {/* Export Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => getExportHandler()('json')}
            disabled={isLoading}
          >
            <FileJson className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => getExportHandler()('csv')}
            disabled={isLoading}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <ErrorBoundary>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <ErrorBoundary>
              <OverviewTab
                tasks={tasks}
                resources={resources}
                isLoading={isLoading}
              />
            </ErrorBoundary>
          </TabsContent>

          <TabsContent value="compliance" className="mt-6">
            <ErrorBoundary>
              <ComplianceDashboard
                tasks={tasks}
                threshold={95}
                period="week"
                isLoading={tasksLoading}
              />
            </ErrorBoundary>
          </TabsContent>

          <TabsContent value="timeline" className="mt-6">
            <ErrorBoundary>
              <ProtectionTimeline
                resources={resources}
                tasks={tasks}
                isLoading={isLoading}
              />
            </ErrorBoundary>
          </TabsContent>

          <TabsContent value="resources" className="mt-6">
            <ErrorBoundary>
              <Card>
                <CardHeader>
                  <CardTitle>Resource Details</CardTitle>
                  <CardDescription>
                    Detailed information about all protected resources
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-12 w-full" />
                    </div>
                  ) : resources.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No resources found. Add resources to start tracking.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {resources.map(resource => (
                        <div
                          key={resource.id}
                          className="flex items-center justify-between p-4 border rounded-lg"
                        >
                          <div>
                            <p className="font-medium">{resource.name}</p>
                            <p className="text-sm text-muted-foreground">
                              ID: {resource.id}
                            </p>
                            {resource.type && (
                              <p className="text-sm text-muted-foreground">
                                Type: {resource.type}
                              </p>
                            )}
                          </div>
                          <div className="text-right text-sm text-muted-foreground">
                            <p>Created: {new Date(resource.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </ErrorBoundary>
          </TabsContent>
        </Tabs>
      </ErrorBoundary>
    </div>
  )
}
